import { useQuery } from "@tanstack/react-query";
import { ChallengeEventType } from "../directus/collections";
import { GaripalliIDB } from "../extensions/idb";
import { useStatsQuery } from "../extensions/sw/modules/stats/hooks";
import { StatsQuery } from "../extensions/sw/modules/stats/types";
import { WorkerQueryTransferModes } from "../libs/workers/consts";
import { WorkerQueryOptions } from "../libs/workers/types";
import { useIndexedDb } from "../providers";
import { getUniquMatchesIds } from "../services/enigmaStats.service";

export function useEnigmaAllMatches(
  cityEscapeIds?: string | null,
  enigmaId?: string,
  filters?: Record<string, any>,
  opts?: WorkerQueryOptions
) {
  const { getDb } = useIndexedDb();
  return useQuery({
    queryKey: ["enigmaTotalMatches", cityEscapeIds, enigmaId, filters],
    queryFn: async () => {
      const db: GaripalliIDB = await getDb();
      try {
        return await getUniquMatchesIds(db, cityEscapeIds, enigmaId);
      } finally {
        db.close();
      }
    },
    enabled: cityEscapeIds !== undefined,
  });
}

export function useEnigmaTotalStartsCount(
  cityEscapeIds?: string | null,
  enigmaId?: string,
  filters?: Record<string, any>,
  opts?: WorkerQueryOptions
) {
  return useStatsQuery(
    {
      type: StatsQuery.TOTAL_ENIGMA_STARTS,
      data: { cityEscapeIds, enigmaId, filters },
      opts,
    },
    { enabled: cityEscapeIds !== undefined }
  );
}

export function useEnigmaTotalMatchesCount(
  cityEscapeIds?: string | null,
  enigmaId?: string,
  filters?: Record<string, any>,
  opts?: WorkerQueryOptions
) {
  return useStatsQuery(
    {
      type: StatsQuery.ENIGMA_TOTAL_MATCHES,
      data: { cityEscapeIds, enigmaId, filters },
      opts: opts,
    },
    {
      enabled: cityEscapeIds !== undefined,
    }
  );
}

export function useEnigmaSuccessRate(
  cityEscapeIds?: string | null,
  enigmaId?: string,
  filters?: Record<string, any>,
  opts?: WorkerQueryOptions
) {
  return useStatsQuery(
    {
      type: StatsQuery.SUCCESS_RATE,
      data: { cityEscapeIds, enigmaId, filters },
      opts,
    },
    { enabled: cityEscapeIds !== undefined }
  );
}

export function useEnigmaFailureRate(
  cityEscapeIds?: string | null,
  enigmaId?: string,
  filters?: Record<string, any>,
  opts?: WorkerQueryOptions
) {
  return useStatsQuery(
    {
      type: StatsQuery.FAILURE_RATE,
      data: { cityEscapeIds, enigmaId, filters },
      opts,
    },
    { enabled: cityEscapeIds !== undefined }
  );
}

export function useEnigmaDropRate(
  cityEscapeIds?: string | null,
  enigmaId?: string,
  filters?: Record<string, any>,
  opts?: WorkerQueryOptions
) {
  return useStatsQuery(
    {
      type: StatsQuery.DROP_RATE,
      data: { cityEscapeIds, enigmaId, filters },
      opts,
    },
    { enabled: cityEscapeIds !== undefined }
  );
}

export function useEnigmaBugsRate(
  cityEscapeIds?: string | null,
  enigmaId?: string,
  filters?: Record<string, any>,
  opts?: WorkerQueryOptions
) {
  return useStatsQuery(
    {
      type: StatsQuery.BUGS_RATE,
      data: { cityEscapeIds, enigmaId, filters },
      opts,
    },
    { enabled: cityEscapeIds !== undefined }
  );
}
export function useEnigmaAvgTime(
  cityEscapeIds?: string | null,
  enigmaId?: string,
  filters?: Record<string, any>,
  opts?: WorkerQueryOptions
) {
  return useStatsQuery(
    {
      type: StatsQuery.AVERAGE_TIME,
      data: { cityEscapeIds, enigmaId, filters },
      opts,
    },
    { enabled: cityEscapeIds !== undefined }
  );
}

export function useEnigmaAvgErrors(
  cityEscapeIds?: string | null,
  enigmaId?: string,
  filters?: Record<string, any>,
  opts?: WorkerQueryOptions
) {
  return useStatsQuery(
    {
      type: StatsQuery.AVERAGE_ERRORS,
      data: { cityEscapeIds, enigmaId, filters },
      opts,
    },
    { enabled: cityEscapeIds !== undefined }
  );
}

export function useEnigmaAvgHints(
  cityEscapeIds?: string | null,
  enigmaId?: string,
  filters?: Record<string, any>,
  opts?: WorkerQueryOptions
) {
  return useStatsQuery(
    {
      type: StatsQuery.AVERAGE_HINTS,
      data: { cityEscapeIds, enigmaId, filters },
      opts,
    },
    { enabled: cityEscapeIds !== undefined }
  );
}

export function useEnigmaNoHintsProvidedRate(
  cityEscapeIds?: string | null,
  enigmaId?: string,
  filters?: Record<string, any>,
  opts?: WorkerQueryOptions
) {
  return useStatsQuery(
    {
      type: StatsQuery.NO_HINTS_PROVIDED_RATE,
      data: { cityEscapeIds, enigmaId, filters },
      opts,
    },
    { enabled: cityEscapeIds !== undefined }
  );
}

export function useEnigmaPaths(
  cityEscapeIds?: string | null,
  enigmaId?: string,
  filters?: Record<string, any>,
  opts?: WorkerQueryOptions
) {
  return useStatsQuery(
    {
      type: StatsQuery.PATHS,
      data: { cityEscapeIds, enigmaId, filters },
      opts,
    },
    { enabled: cityEscapeIds !== undefined }
  );
}

export function useMatchHints(
  cityEscapeIds?: string | null,
  enigmaId?: string,
  filters?: Record<string, any>,
  opts?: WorkerQueryOptions
) {
  return useStatsQuery(
    {
      type: StatsQuery.MATCH_HINTS,
      data: { cityEscapeIds, enigmaId, filters },
      opts,
    },
    { enabled: cityEscapeIds !== undefined }
  );
}

export function useMatchesTimelines(
  cityEscapeIds?: string | null,
  enigmaId?: string,
  filters?: Record<string, any>,
  opts?: WorkerQueryOptions
) {
  console.trace("useMatchesTimelines called with:", {
    cityEscapeId: cityEscapeIds,
    enigmaId,
  });
  return useStatsQuery(
    {
      type: StatsQuery.MATCHES_TIMELINES,
      data: { cityEscapeIds, enigmaId, filters },
      opts: {
        ...opts,
        response: {
          useCompression: false,
          transferMode: WorkerQueryTransferModes.ARRAY_BUFFER,
        },
      },
    },
    {
      staleTime: 1000 * 60 * 5, // 5 minutes
      refetchOnMount: false,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
      gcTime: 1000 * 60 * 30, // 30 minutes
    }
  );
}

export function useAvgTimeToEnigmaEvent(
  {
    eventType,
    value,
  }: { eventType?: ChallengeEventType | ChallengeEventType[]; value?: string },
  cityEscapeId?: string | null,
  enigmaId?: string,
  filters?: Record<string, any>,
  opts?: WorkerQueryOptions
) {
  return useStatsQuery(
    {
      type: StatsQuery.AVG_TIME_TO_ENIGMA_EVENT,
      data: {
        cityEscapeId,
        targetId: enigmaId,
        eventType,
        value,
        filters,
      },
      opts,
    },
    { enabled: cityEscapeId !== undefined }
  );
}
